import React, { useState, useEffect } from 'react';
import { X, Plus, Edit, Trash2, Save, AlertCircle, CheckCircle, ChevronRight, ChevronDown } from 'lucide-react';
import { useBrand } from '../contexts/BrandContext';
import { useToast } from '../contexts/ToastContext';
import { useConfirm } from '../hooks/useConfirm';
import ConfirmModal from './ConfirmModal';
import { taskService, TaskTemplate, CreateTaskTemplateInput, TaskTemplateSubtask } from '../lib/taskService';
import { getPipelineStages, PipelineStage } from '../lib/crmService';

interface TaskTemplateManagerProps {
  onClose: () => void;
}

const TaskTemplateManager: React.FC<TaskTemplateManagerProps> = ({ onClose }) => {
  const { selectedBrand } = useBrand();
  const { success, error } = useToast();
  const { confirm, confirmState, closeConfirm } = useConfirm();
  const [templates, setTemplates] = useState<TaskTemplate[]>([]);
  const [stages, setStages] = useState<PipelineStage[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<TaskTemplate | null>(null);
  const [formData, setFormData] = useState<CreateTaskTemplateInput>({
    brand_id: selectedBrand?.id || '',
    name: '',
    description: '',
    due_days_offset: 7,
    priority: 'medium',
    auto_trigger_stage_id: undefined,
    is_active: true,
  });
  const [expandedTemplates, setExpandedTemplates] = useState<Set<string>>(new Set());
  const [editingSubtask, setEditingSubtask] = useState<TaskTemplateSubtask | null>(null);
  const [showSubtaskForm, setShowSubtaskForm] = useState(false);
  const [selectedTemplateForSubtask, setSelectedTemplateForSubtask] = useState<string | null>(null);
  const [subtaskFormData, setSubtaskFormData] = useState({
    title: '',
    description: '',
    due_days_offset_from_parent: 0,
    priority: 'medium' as 'low' | 'medium' | 'high' | 'urgent',
  });

  useEffect(() => {
    if (selectedBrand) {
      loadData();
    }
  }, [selectedBrand]);

  const loadData = async () => {
    if (!selectedBrand) return;

    try {
      setLoading(true);
      const [templatesData, stagesData] = await Promise.all([
        taskService.getTaskTemplates(selectedBrand.id),
        getPipelineStages(selectedBrand.id),
      ]);
      setTemplates(templatesData);
      setStages(stagesData);
    } catch (err) {
      error('Failed to load task templates');
      console.error('Error loading data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingTemplate(null);
    setFormData({
      brand_id: selectedBrand?.id || '',
      name: '',
      description: '',
      due_days_offset: 7,
      priority: 'medium',
      auto_trigger_stage_id: undefined,
      is_active: true,
    });
    setShowForm(true);
  };

  const handleEdit = (template: TaskTemplate) => {
    setEditingTemplate(template);
    setFormData({
      brand_id: template.brand_id,
      name: template.name,
      description: template.description || '',
      due_days_offset: template.due_days_offset,
      priority: template.priority,
      auto_trigger_stage_id: template.auto_trigger_stage_id || undefined,
      is_active: template.is_active,
    });
    setShowForm(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedBrand) return;

    try {
      if (editingTemplate) {
        await taskService.updateTaskTemplate(editingTemplate.id, formData);
        success('Template updated successfully');
      } else {
        await taskService.createTaskTemplate(formData);
        success('Template created successfully');
      }

      setShowForm(false);
      loadData();
    } catch (err) {
      error('Failed to save template');
      console.error('Error saving template:', err);
    }
  };

  const handleDelete = async (templateId: string) => {
    const confirmed = await confirm({
      title: 'Delete Template',
      message: 'Are you sure you want to delete this template? Tasks created from this template will not be affected.',
      confirmLabel: 'Delete',
      cancelLabel: 'Cancel',
      variant: 'danger',
    });

    if (!confirmed) return;

    setTemplates(prev => prev.filter(t => t.id !== templateId));

    try {
      const result = await taskService.deleteTaskTemplate(templateId);
      if (result) {
        success('Template deleted successfully');
      } else {
        error('Failed to delete template');
        loadData();
      }
    } catch (err) {
      error('Failed to delete template');
      console.error('Error deleting template:', err);
      loadData();
    }
  };

  const handleToggleActive = async (template: TaskTemplate) => {
    const newActiveState = !template.is_active;

    setTemplates(prev => prev.map(t =>
      t.id === template.id ? { ...t, is_active: newActiveState } : t
    ));

    try {
      await taskService.updateTaskTemplate(template.id, {
        is_active: newActiveState,
      });
      success(`Template ${newActiveState ? 'activated' : 'deactivated'}`);
    } catch (err) {
      error('Failed to update template');
      console.error('Error toggling template:', err);
      setTemplates(prev => prev.map(t =>
        t.id === template.id ? { ...t, is_active: !newActiveState } : t
      ));
    }
  };

  const toggleTemplateExpanded = (templateId: string) => {
    setExpandedTemplates(prev => {
      const newSet = new Set(prev);
      if (newSet.has(templateId)) {
        newSet.delete(templateId);
      } else {
        newSet.add(templateId);
      }
      return newSet;
    });
  };

  const handleAddSubtask = (templateId: string) => {
    setSelectedTemplateForSubtask(templateId);
    setEditingSubtask(null);
    setSubtaskFormData({
      title: '',
      description: '',
      due_days_offset_from_parent: 0,
      priority: 'medium',
    });
    setShowSubtaskForm(true);
  };

  const handleEditSubtask = (subtask: TaskTemplateSubtask) => {
    setSelectedTemplateForSubtask(subtask.template_id);
    setEditingSubtask(subtask);
    setSubtaskFormData({
      title: subtask.title,
      description: subtask.description || '',
      due_days_offset_from_parent: subtask.due_days_offset_from_parent,
      priority: subtask.priority,
    });
    setShowSubtaskForm(true);
  };

  const handleSubtaskSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedTemplateForSubtask) return;

    try {
      if (editingSubtask) {
        await taskService.updateTemplateSubtask(editingSubtask.id, subtaskFormData);
        success('Subtask updated successfully');
      } else {
        const template = templates.find(t => t.id === selectedTemplateForSubtask);
        const orderIndex = template?.subtasks?.length ?? 0;

        await taskService.createTemplateSubtask({
          template_id: selectedTemplateForSubtask,
          ...subtaskFormData,
          order_index: orderIndex,
        });
        success('Subtask created successfully');
      }
      setShowSubtaskForm(false);
      loadData();
    } catch (err) {
      error('Failed to save subtask');
      console.error('Error saving subtask:', err);
    }
  };

  const handleDeleteSubtask = async (subtaskId: string) => {
    const confirmed = await confirm({
      title: 'Delete Subtask',
      message: 'Are you sure you want to delete this subtask?',
      confirmLabel: 'Delete',
      cancelLabel: 'Cancel',
      variant: 'danger',
    });

    if (!confirmed) return;

    setTemplates(prev => prev.map(template => ({
      ...template,
      subtasks: template.subtasks?.filter(s => s.id !== subtaskId)
    })));

    try {
      const result = await taskService.deleteTemplateSubtask(subtaskId);
      if (result) {
        success('Subtask deleted successfully');
      } else {
        error('Failed to delete subtask');
        loadData();
      }
    } catch (err) {
      error('Failed to delete subtask');
      console.error('Error deleting subtask:', err);
      loadData();
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent': return 'text-red-600 bg-red-100';
      case 'high': return 'text-orange-600 bg-orange-100';
      case 'medium': return 'text-blue-600 bg-blue-100';
      case 'low': return 'text-gray-600 bg-gray-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  if (!selectedBrand) {
    return null;
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-xl font-bold text-gray-900">Task Templates</h2>
            <p className="text-sm text-gray-600 mt-1">
              Create reusable task templates that can be auto-applied when opportunities enter specific stages
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex items-center justify-center h-32">
              <p className="text-gray-500">Loading templates...</p>
            </div>
          ) : showForm ? (
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                  <div className="text-sm text-blue-900">
                    <p className="font-medium mb-1">About Task Templates</p>
                    <p>
                      Templates can be manually applied to opportunities or automatically triggered
                      when an opportunity enters a specific pipeline stage. Tasks will be assigned to
                      the opportunity owner by default.
                    </p>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Template Name *
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="e.g., Follow up after proposal"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows={3}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Detailed task instructions..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Due Days After Trigger *
                  </label>
                  <input
                    type="number"
                    value={formData.due_days_offset}
                    onChange={(e) => setFormData({ ...formData, due_days_offset: parseInt(e.target.value) || 0 })}
                    required
                    min="0"
                    max="365"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  <p className="text-xs text-gray-500 mt-1">Days until task is due (0-365)</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Priority *
                  </label>
                  <select
                    value={formData.priority}
                    onChange={(e) => setFormData({ ...formData, priority: e.target.value as any })}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                    <option value="urgent">Urgent</option>
                  </select>
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Auto-Trigger Stage (Optional)
                  </label>
                  <select
                    value={formData.auto_trigger_stage_id || ''}
                    onChange={(e) => setFormData({ ...formData, auto_trigger_stage_id: e.target.value || undefined })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">No auto-trigger (Manual only)</option>
                    {stages.map((stage) => (
                      <option key={stage.id} value={stage.id}>
                        {stage.name}
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    If selected, tasks will be automatically created when opportunities enter this stage
                  </p>
                </div>

                <div className="md:col-span-2">
                  <label className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={formData.is_active}
                      onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                      className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                    <span className="text-sm font-medium text-gray-700">Template is active</span>
                  </label>
                  <p className="text-xs text-gray-500 mt-1 ml-6">
                    Inactive templates won't auto-trigger but can still be manually applied
                  </p>
                </div>
              </div>

              <div className="flex gap-3 justify-end pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => setShowForm(false)}
                  className="px-4 py-2 text-gray-700 font-medium border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2 shadow-sm transition-all"
                >
                  <Save className="w-4 h-4" />
                  {editingTemplate ? 'Update' : 'Create'} Template
                </button>
              </div>
            </form>
          ) : (
            <div className="space-y-4">
              <button
                onClick={handleCreate}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 border-2 border-dashed border-gray-300 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-colors text-gray-600 hover:text-blue-600"
              >
                <Plus className="w-5 h-5" />
                Create New Template
              </button>

              {templates.length === 0 ? (
                <div className="text-center py-12 text-gray-400">
                  <p className="text-sm">No task templates yet</p>
                  <p className="text-xs mt-1">Create your first template to get started</p>
                </div>
              ) : (
                templates.map((template) => (
                  <div
                    key={template.id}
                    className={`border rounded-lg p-4 transition-colors ${
                      template.is_active
                        ? 'border-gray-200 bg-white'
                        : 'border-gray-200 bg-gray-50 opacity-60'
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <h3 className="text-lg font-semibold text-gray-900">
                            {template.name}
                          </h3>
                          <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${getPriorityColor(template.priority)}`}>
                            {template.priority}
                          </span>
                          {!template.is_active && (
                            <span className="px-2 py-0.5 text-xs font-medium bg-gray-200 text-gray-600 rounded-full">
                              Inactive
                            </span>
                          )}
                        </div>
                        {template.description && (
                          <p className="text-sm text-gray-600 mb-3">{template.description}</p>
                        )}
                        <div className="flex flex-wrap items-center gap-4 text-sm">
                          <div className="flex items-center gap-1 text-gray-600">
                            <span className="font-medium">Due in:</span>
                            <span>{template.due_days_offset} days</span>
                          </div>
                          {template.auto_trigger_stage_id && template.pipeline_stage && (
                            <div className="flex items-center gap-2">
                              <CheckCircle className="w-4 h-4 text-green-600" />
                              <span className="text-gray-600">Auto-triggers on:</span>
                              <span
                                className="font-medium px-2 py-0.5 rounded text-xs"
                                style={{
                                  backgroundColor: `${template.pipeline_stage.color}20`,
                                  color: template.pipeline_stage.color,
                                }}
                              >
                                {template.pipeline_stage.name}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2 ml-4">
                        <button
                          onClick={() => handleAddSubtask(template.id)}
                          className="p-2 hover:bg-blue-50 rounded-lg transition-colors"
                          title="Add Subtask"
                        >
                          <Plus className="w-5 h-5 text-blue-600" />
                        </button>
                        <button
                          onClick={() => handleToggleActive(template)}
                          className={`p-2 rounded-lg transition-colors ${
                            template.is_active
                              ? 'hover:bg-gray-100 text-gray-600'
                              : 'hover:bg-green-50 text-green-600'
                          }`}
                          title={template.is_active ? 'Deactivate' : 'Activate'}
                        >
                          <CheckCircle className="w-5 h-5" />
                        </button>
                        <button
                          onClick={() => handleEdit(template)}
                          className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                          title="Edit"
                        >
                          <Edit className="w-5 h-5 text-gray-600" />
                        </button>
                        <button
                          onClick={() => handleDelete(template.id)}
                          className="p-2 hover:bg-red-50 rounded-lg transition-colors"
                          title="Delete"
                        >
                          <Trash2 className="w-5 h-5 text-red-600" />
                        </button>
                        {template.subtasks && template.subtasks.length > 0 && (
                          <button
                            onClick={() => toggleTemplateExpanded(template.id)}
                            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                            title={expandedTemplates.has(template.id) ? "Collapse subtasks" : "Expand subtasks"}
                          >
                            {expandedTemplates.has(template.id) ? (
                              <ChevronDown className="w-5 h-5 text-gray-600" />
                            ) : (
                              <ChevronRight className="w-5 h-5 text-gray-600" />
                            )}
                          </button>
                        )}
                      </div>
                    </div>

                    {template.subtasks && template.subtasks.length > 0 && expandedTemplates.has(template.id) && (
                      <div className="mt-4 ml-6 space-y-2 border-l-2 border-gray-200 pl-4">
                        <h4 className="text-sm font-medium text-gray-700 mb-2">Subtasks ({template.subtasks.length})</h4>
                        {template.subtasks.map((subtask) => (
                          <div
                            key={subtask.id}
                            className="bg-gray-50 border border-gray-200 rounded-lg p-3"
                          >
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-1">
                                  <h5 className="text-sm font-medium text-gray-900">{subtask.title}</h5>
                                  <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${getPriorityColor(subtask.priority)}`}>
                                    {subtask.priority}
                                  </span>
                                </div>
                                {subtask.description && (
                                  <p className="text-xs text-gray-600 mb-2">{subtask.description}</p>
                                )}
                                <div className="text-xs text-gray-500">
                                  <span className="font-medium">Due:</span> {subtask.due_days_offset_from_parent} days after parent task
                                </div>
                              </div>
                              <div className="flex items-center gap-1 ml-2">
                                <button
                                  onClick={() => handleEditSubtask(subtask)}
                                  className="p-1.5 hover:bg-gray-200 rounded-lg transition-colors"
                                  title="Edit subtask"
                                >
                                  <Edit className="w-4 h-4 text-gray-600" />
                                </button>
                                <button
                                  onClick={() => handleDeleteSubtask(subtask.id)}
                                  className="p-1.5 hover:bg-red-100 rounded-lg transition-colors"
                                  title="Delete subtask"
                                >
                                  <Trash2 className="w-4 h-4 text-red-600" />
                                </button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          )}
        </div>

        <div className="border-t border-gray-200 p-4 bg-gray-50">
          <button
            onClick={onClose}
            className="w-full px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors font-medium"
          >
            Close
          </button>
        </div>
      </div>

      {showSubtaskForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h3 className="text-lg font-bold text-gray-900">
                {editingSubtask ? 'Edit Subtask' : 'Add Subtask'}
              </h3>
              <button
                onClick={() => setShowSubtaskForm(false)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            <form onSubmit={handleSubtaskSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Subtask Title *
                </label>
                <input
                  type="text"
                  value={subtaskFormData.title}
                  onChange={(e) => setSubtaskFormData({ ...subtaskFormData, title: e.target.value })}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="e.g., Review contract terms"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description
                </label>
                <textarea
                  value={subtaskFormData.description}
                  onChange={(e) => setSubtaskFormData({ ...subtaskFormData, description: e.target.value })}
                  rows={3}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Detailed instructions for this subtask..."
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Days After Parent Task *
                  </label>
                  <input
                    type="number"
                    value={subtaskFormData.due_days_offset_from_parent}
                    onChange={(e) => setSubtaskFormData({ ...subtaskFormData, due_days_offset_from_parent: parseInt(e.target.value) || 0 })}
                    required
                    min="-30"
                    max="30"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  <p className="text-xs text-gray-500 mt-1">Days relative to parent task due date (-30 to 30)</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Priority *
                  </label>
                  <select
                    value={subtaskFormData.priority}
                    onChange={(e) => setSubtaskFormData({ ...subtaskFormData, priority: e.target.value as any })}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                    <option value="urgent">Urgent</option>
                  </select>
                </div>
              </div>

              <div className="flex gap-3 justify-end pt-4">
                <button
                  type="button"
                  onClick={() => setShowSubtaskForm(false)}
                  className="px-4 py-2 text-gray-700 font-medium border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
                >
                  <Save className="w-4 h-4" />
                  {editingSubtask ? 'Update' : 'Create'} Subtask
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <ConfirmModal
        isOpen={confirmState.isOpen}
        onClose={closeConfirm}
        onConfirm={confirmState.onConfirm}
        title={confirmState.title}
        message={confirmState.message}
        confirmLabel={confirmState.confirmLabel}
        cancelLabel={confirmState.cancelLabel}
        variant={confirmState.variant}
        brandColor={selectedBrand?.primary_color}
      />
    </div>
  );
};

export default TaskTemplateManager;
